import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { EmptyState, Spinner } from '../components/ui';
import { useAuth } from './AuthContext';
import { hasAtLeast, type Role } from './roles';

/**
 * Gate in front of the shell.
 *
 *  - session still resolving          → spinner (never a flash of the login page)
 *  - no session (401 from /auth/me)   → `/login`, carrying `next` so the shell
 *                                       could return the user where they were,
 *                                       plus `err=session` when we were bounced
 *                                       mid-session rather than arriving cold
 *  - `AUTH_DISABLED` (dev / e2e)      → through: there is no login flow at all
 *  - `minRole` not met                → a 403 empty state; the api is still the
 *                                       authority, this only hides the UI
 */
export function ProtectedRoute({ children, minRole }: { children: ReactNode; minRole?: Role }) {
  const { status, role, error } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="interstitial">
        <Spinner label="Проверяем сессию" />
      </div>
    );
  }

  if (status === 'anonymous') {
    const params = new URLSearchParams();
    params.set('next', `${location.pathname}${location.search}`);
    if (error?.status === 401) params.set('err', 'session');
    return <Navigate to={`/login?${params.toString()}`} replace />;
  }

  if (minRole && !hasAtLeast(role, minRole)) {
    return (
      <div className="card">
        <EmptyState
          title="403 — не ваша роль"
          hint={`Для этого экрана нужна роль «${minRole}» или выше. Ваша роль его не открывает (API отказал бы и в самом запросе).`}
        />
      </div>
    );
  }

  return <>{children}</>;
}
